import React, { useEffect, useMemo, useRef, useState } from "react";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Paper,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import muiTheme from "../theme/muiTheme";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { limparCnpj, mascararCnpj, validarCnpj } from "../utils/documento";

// ==========================================================
// ALTERAÇÕES CONTRATUAIS - CADASTRO DE EMPRESAS (seção 3)
// Empresa: apelido, razão social, CNPJ e empresa-mãe/grupo.
// ==========================================================

const API_EMPRESAS = "/api/alteracoes-contratuais/empresas";

async function chamarApi(caminho = "", opcoes = {}) {
  const tk = localStorage.getItem("token");
  const resposta = await fetch(`${API_EMPRESAS}${caminho}`, {
    ...opcoes,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}`, ...(opcoes.headers || {}) },
  });
  const dados = await resposta.json().catch(() => ({}));
  if (!resposta.ok) throw new Error(dados.erro || "Não foi possível concluir a operação.");
  return dados;
}

const formVazio = { cnpj: "", razaoSocial: "", apelido: "", empresaMaeId: "", ativa: true, observacoes: "" };

// CNPJ numérico ou alfanumérico (letras A-Z nas 12 primeiras posições).
const numeros = (v) => limparCnpj(v);
const maiusculas = (v) => String(v ?? "").toLocaleUpperCase("pt-BR");

const formatarCnpj = (v) => mascararCnpj(v);

const cnpjValido = (valor) => validarCnpj(valor);

const normalizar = (texto) =>
  String(texto ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

// Pesquisa com "%" separando termos (ex.: "serbom%ativa").
const corresponde = (valor, termo) =>
  normalizar(termo)
    .split("%")
    .map((t) => t.trim())
    .filter(Boolean)
    .every((t) => normalizar(valor).includes(t));

const estiloCabecalho = {
  backgroundColor: "var(--ig-f-1e1e1e)",
  color: "var(--ig-t-ffffff)",
  fontWeight: "bold",
  borderBottom: "1px solid var(--ig-b-444444)",
  whiteSpace: "nowrap",
};

const estiloBotaoAcao = (cor, corHover) => ({
  backgroundColor: cor,
  color: "#fff",
  borderRadius: "4px",
  mx: 0.25,
  width: "auto",
  "&:hover": { backgroundColor: corHover },
});

const estiloCampo = (somenteLeitura, erro) => ({
  "& .MuiInputLabel-root": { color: erro ? "#f44336" : "var(--ig-t-ffffff)", fontWeight: "bold" },
  "& .MuiOutlinedInput-root": {
    backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
    color: "var(--ig-t-ffffff)",
    "& fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-444444)" },
    "&:hover fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-888888)" },
  },
  "& input, & textarea": { color: "var(--ig-t-ffffff)" },
  "& .MuiFormHelperText-root": { color: erro ? "#f44336" : "var(--ig-t-9ca3af)" },
});

const estiloSelect = (somenteLeitura) => ({
  backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
  color: "var(--ig-t-ffffff)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : "var(--ig-b-444444)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : "var(--ig-b-888888)" },
  "& .MuiSvgIcon-root": { color: somenteLeitura ? "transparent" : "var(--ig-t-ffffff)" },
});

export default function SocietarioEmpresas() {
  const [empresas, setEmpresas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [processando, setProcessando] = useState(false);

  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(10);
  const [gridMinimizado, setGridMinimizado] = useState(false);

  const cnpjInputRef = useRef(null);

  // form inferior: null = novo, valor = id selecionado
  const [editandoId, setEditandoId] = useState(null);
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [form, setForm] = useState({ ...formVazio });
  const [errosForm, setErrosForm] = useState({});

  // Consulta de CNPJ (BrasilAPI)
  const [consultandoCnpj, setConsultandoCnpj] = useState(false);
  const [mensagemCnpj, setMensagemCnpj] = useState("");
  const versaoConsultaRef = useRef(0);

  const [confirmDelete, setConfirmDelete] = useState({ aberto: false, id: null });

  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  const mostrarSnack = (msg, severity = "success") => setSnack({ aberto: true, msg, severity });

  // permissões (níveis do sistema)
  const token = localStorage.getItem("token");
  let tipoUsuario = "";
  if (token) {
    try {
      tipoUsuario = normalizar(jwtDecode(token).tipo);
    } catch (_) {}
  }
  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["administrador"].includes(tipoUsuario);

  const carregarEmpresas = async () => {
    setCarregando(true);
    try {
      const lista = await chamarApi();
      setEmpresas(Array.isArray(lista) ? lista : []);
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (podeVisualizar) carregarEmpresas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeVisualizar]);

  // ── Pesquisa automática
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltro(inputFiltro);
      setPagina(0);
    }, 400);
    return () => clearTimeout(t);
  }, [inputFiltro]);

  const nomeEmpresaMae = (empresa) => empresas.find((e) => String(e.id) === String(empresa.empresaMaeId))?.apelido || "—";

  const empresasOrdenadas = useMemo(() => [...empresas].sort((a, b) => String(a.apelido).localeCompare(String(b.apelido), "pt-BR")), [empresas]);

  const empresasFiltradas = useMemo(() => {
    if (!filtro.trim()) return empresasOrdenadas;
    return empresasOrdenadas.filter((e) =>
      [e.apelido, e.razaoSocial, e.cnpj, numeros(e.cnpj), nomeEmpresaMae(e), e.ativa ? "ativa" : "inativa"].some((v) => corresponde(v, filtro)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresasOrdenadas, filtro]);

  const empresasPagina = empresasFiltradas.slice(pagina * porPagina, pagina * porPagina + porPagina);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPagina(0);
  };
  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  const handleCopiar = async () => {
    const texto = empresasFiltradas.map((e) => `${e.apelido} - ${e.cnpj}`).join(", ");
    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }
    const ok = await copiarTexto(texto);
    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  const handleCSV = () => {
    const BOM = "﻿";
    const header = ["Apelido", "Razão Social", "CNPJ", "Empresa-Mãe", "Situação", "Observações"];
    const csvEscape = (value) => {
      const texto = String(value ?? "");
      return /[;"\n\r]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
    };
    const linhas = empresasFiltradas.map((e) =>
      [e.apelido, e.razaoSocial, e.cnpj, nomeEmpresaMae(e), e.ativa ? "Ativa" : "Inativa", e.observacoes].map(csvEscape),
    );
    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "empresas-alteracoes-contratuais.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const limparConsultaCnpj = () => {
    versaoConsultaRef.current += 1;
    setConsultandoCnpj(false);
    setMensagemCnpj("");
  };

  // ── Visualizar (preenche form inferior)
  const handleVisualizar = (empresa) => {
    limparConsultaCnpj();
    setEditandoId(empresa.id);
    setSomenteLeitura(true);
    setForm({
      cnpj: formatarCnpj(empresa.cnpj),
      razaoSocial: empresa.razaoSocial || "",
      apelido: empresa.apelido || "",
      empresaMaeId: empresa.empresaMaeId ? String(empresa.empresaMaeId) : "",
      ativa: Boolean(empresa.ativa),
      observacoes: empresa.observacoes || "",
    });
    setErrosForm({});
    setGridMinimizado(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleHabilitarEdicao = () => {
    setSomenteLeitura(false);
    setTimeout(() => cnpjInputRef.current?.focus(), 50);
  };

  const handleCancelarForm = () => {
    limparConsultaCnpj();
    setEditandoId(null);
    setSomenteLeitura(false);
    setForm({ ...formVazio });
    setErrosForm({});
    setGridMinimizado(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const atualizarCampo = (campo, valor) => {
    if (somenteLeitura) return;
    setForm((p) => ({ ...p, [campo]: valor }));
    if (errosForm[campo]) setErrosForm((p) => ({ ...p, [campo]: undefined }));
  };

  // Consulta a Razão Social pelo CNPJ ao completar os 14 dígitos.
  const consultarCnpj = async (cnpjDigitado) => {
    const cnpj = numeros(cnpjDigitado);
    if (cnpj.length !== 14) return;
    if (!cnpjValido(cnpj)) {
      setMensagemCnpj("");
      setErrosForm((p) => ({ ...p, cnpj: "CNPJ inválido. Confira os dígitos verificadores." }));
      return;
    }

    const versao = ++versaoConsultaRef.current;
    setConsultandoCnpj(true);
    setMensagemCnpj("Consultando CNPJ...");

    try {
      const resposta = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      if (!resposta.ok) throw new Error(resposta.status === 404 ? "CNPJ não encontrado na consulta pública. Informe a Razão Social." : "");
      const dados = await resposta.json();
      if (versao !== versaoConsultaRef.current) return;

      setForm((p) =>
        numeros(p.cnpj) !== cnpj
          ? p
          : {
              ...p,
              razaoSocial: maiusculas(dados.razao_social || p.razaoSocial),
              apelido: p.apelido || maiusculas(dados.nome_fantasia || ""),
            },
      );
      setErrosForm((p) => ({ ...p, razaoSocial: undefined }));
      setMensagemCnpj("Dados cadastrais localizados.");
    } catch (erro) {
      if (versao === versaoConsultaRef.current) {
        setMensagemCnpj(erro instanceof TypeError || !erro.message ? "Consulta de CNPJ indisponível no momento. Informe a Razão Social." : erro.message);
      }
    } finally {
      if (versao === versaoConsultaRef.current) setConsultandoCnpj(false);
    }
  };

  const validarForm = () => {
    const erros = {};
    if (!cnpjValido(form.cnpj)) erros.cnpj = "CNPJ inválido. Confira os dígitos verificadores.";
    if (!form.razaoSocial.trim()) erros.razaoSocial = "Razão Social é obrigatória.";
    if (!form.apelido.trim()) erros.apelido = "Apelido é obrigatório.";
    return erros;
  };

  // ── Salvar (criar ou editar)
  const handleSalvar = async () => {
    if (processando) return;
    const erros = validarForm();
    if (Object.keys(erros).length > 0) {
      setErrosForm(erros);
      cnpjInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErrosForm({});

    const corpo = {
      apelido: maiusculas(form.apelido.trim()),
      razaoSocial: maiusculas(form.razaoSocial.trim()),
      cnpj: numeros(form.cnpj),
      empresaMaeId: form.empresaMaeId ? Number(form.empresaMaeId) : null,
      ativa: form.ativa,
      observacoes: maiusculas(form.observacoes.trim()),
    };

    setProcessando(true);
    try {
      await chamarApi(editandoId ? `/${editandoId}` : "", { method: editandoId ? "PUT" : "POST", body: JSON.stringify(corpo) });
      mostrarSnack(editandoId ? "Empresa atualizada!" : "Empresa cadastrada com sucesso!");
      await carregarEmpresas();
      handleCancelarForm();
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    } finally {
      setProcessando(false);
    }
  };

  // ── Excluir
  const handleConfirmarDelete = (id) => {
    document.activeElement?.blur();
    setConfirmDelete({ aberto: true, id });
  };

  const handleExcluirConfirmado = async () => {
    const { id } = confirmDelete;
    setConfirmDelete({ aberto: false, id: null });
    try {
      await chamarApi(`/${id}`, { method: "DELETE" });
      if (editandoId === id) handleCancelarForm();
      await carregarEmpresas();
      mostrarSnack("Empresa excluída.", "warning");
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    }
  };

  if (!podeVisualizar) {
    return (
      <ThemeProvider theme={muiTheme}>
        <Box sx={{ color: "var(--ig-t-ffffff)" }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Empresas
          </Typography>
          <Alert severity="warning">Você não possui permissão para acessar esta tela.</Alert>
        </Box>
      </ThemeProvider>
    );
  }

  // Empresa-mãe: qualquer outra empresa (o backend bloqueia vínculo circular).
  const opcoesEmpresaMae = empresasOrdenadas.filter((e) => e.id !== editandoId);

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        {/* Título */}
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Empresas
        </Typography>

        {/* ── Filtro ─────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Filtrar Empresa"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePesquisar();
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "var(--ig-t-9ca3af)", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": { backgroundColor: "transparent", color: "var(--ig-t-ffffff)" },
              "& input::placeholder": { color: "var(--ig-t-ffffff)", opacity: 1, fontWeight: 500, fontSize: "1rem" },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": { borderColor: "#42a5f5", backgroundColor: "rgba(33, 150, 243, 0.08)" },
            }}
          >
            Limpar Filtro
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{ height: 40, backgroundColor: "#2196f3", fontWeight: 600, textTransform: "none", "&:hover": { backgroundColor: "#1976d2" } }}
          >
            <i className="fas fa-copy" style={{ marginRight: 6 }} />
            Copiar
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{ height: 40, backgroundColor: "#2196f3", fontWeight: 600, textTransform: "none", "&:hover": { backgroundColor: "#1976d2" } }}
          >
            CSV
          </Button>
        </Box>

        {/* ── Minimizar grid ───────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }}>
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        {/* ── Grid ─────────────────────────────────────────────────────────── */}
        <Collapse in={!gridMinimizado}>
          <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: "transparent", boxShadow: "none" }}>
            <Table size="small" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={estiloCabecalho}>Ações</TableCell>
                  <TableCell sx={estiloCabecalho}>Apelido</TableCell>
                  <TableCell sx={{ ...estiloCabecalho, width: "100%" }}>Razão Social</TableCell>
                  <TableCell sx={estiloCabecalho}>CNPJ</TableCell>
                  <TableCell sx={estiloCabecalho}>Empresa-Mãe</TableCell>
                  <TableCell align="center" sx={estiloCabecalho}>
                    Situação
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {empresasPagina.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: "var(--ig-t-888888)", py: 3, borderBottom: "1px solid var(--ig-b-444444)" }}>
                      {carregando ? "Carregando empresas..." : "Nenhuma empresa encontrada."}
                    </TableCell>
                  </TableRow>
                ) : (
                  empresasPagina.map((empresa) => (
                    <TableRow
                      key={empresa.id}
                      sx={{
                        "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" },
                        backgroundColor: editandoId === empresa.id ? "rgba(25,152,168,0.08)" : "transparent",
                      }}
                    >
                      <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                        <Tooltip title="Visualizar / Editar">
                          <IconButton size="small" onClick={() => handleVisualizar(empresa)} sx={estiloBotaoAcao("#1998a8", "#1565c0")}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {podeExcluir && (
                          <Tooltip title="Excluir empresa">
                            <IconButton size="small" onClick={() => handleConfirmarDelete(empresa.id)} sx={estiloBotaoAcao("#d32f2f", "#c62828")}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)", whiteSpace: "nowrap" }}>
                        {empresa.apelido}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)" }}>{empresa.razaoSocial}</TableCell>
                      <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                        {empresa.cnpj}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                        {nomeEmpresaMae(empresa)}
                      </TableCell>
                      <TableCell align="center" sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                        <Chip
                          size="small"
                          label={empresa.ativa ? "Ativa" : "Inativa"}
                          sx={{ backgroundColor: empresa.ativa ? "#2e7d32" : "#555", color: "#fff", fontWeight: "bold" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={empresasFiltradas.length}
              page={pagina}
              onPageChange={(_, p) => setPagina(p)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(e) => {
                setPorPagina(parseInt(e.target.value, 10));
                setPagina(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Empresas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "var(--ig-t-ffffff)",
                borderTop: "1px solid var(--ig-b-444444)",
                "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
                "& .MuiTablePagination-actions button": { color: "var(--ig-t-ffffff)", width: "auto" },
                "& .MuiTablePagination-actions": { display: "flex", alignItems: "center" },
              }}
            />
          </TableContainer>
        </Collapse>

        {/* ── Formulário inferior (visualizar / criar / editar) ────────────── */}
        {(podeCadastrar || (podeVisualizar && editandoId)) && (
          <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>
            <Typography variant="h6" sx={{ mb: 2, color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
              {editandoId && somenteLeitura ? "Visualizar Empresa" : editandoId ? "Editar Empresa" : "Nova Empresa"}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="CNPJ*"
                  size="small"
                  inputRef={cnpjInputRef}
                  value={form.cnpj}
                  onChange={(e) => {
                    const valor = formatarCnpj(e.target.value);
                    limparConsultaCnpj();
                    atualizarCampo("cnpj", valor);
                    if (numeros(valor).length === 14) consultarCnpj(valor);
                  }}
                  error={!!errosForm.cnpj}
                  helperText={errosForm.cnpj || mensagemCnpj || " "}
                  slotProps={{
                    input: {
                      readOnly: somenteLeitura,
                      endAdornment: consultandoCnpj ? (
                        <InputAdornment position="end">
                          <CircularProgress size={18} />
                        </InputAdornment>
                      ) : null,
                    },
                    htmlInput: { maxLength: 18, inputMode: "text" },
                  }}
                  sx={{ flex: 1, minWidth: 220, ...estiloCampo(somenteLeitura, errosForm.cnpj) }}
                />

                <TextField
                  label="Razão Social*"
                  size="small"
                  value={form.razaoSocial}
                  onChange={(e) => atualizarCampo("razaoSocial", maiusculas(e.target.value))}
                  error={!!errosForm.razaoSocial}
                  helperText={errosForm.razaoSocial || (somenteLeitura ? " " : "Preenchida pela consulta do CNPJ; pode ser ajustada.")}
                  slotProps={{ input: { readOnly: somenteLeitura }, htmlInput: { maxLength: 255 } }}
                  sx={{ flex: 3, minWidth: 280, ...estiloCampo(somenteLeitura, errosForm.razaoSocial) }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Apelido*"
                  size="small"
                  value={form.apelido}
                  onChange={(e) => atualizarCampo("apelido", maiusculas(e.target.value))}
                  error={!!errosForm.apelido}
                  helperText={errosForm.apelido}
                  slotProps={{ input: { readOnly: somenteLeitura }, htmlInput: { maxLength: 100 } }}
                  sx={{ flex: 2, minWidth: 220, ...estiloCampo(somenteLeitura, errosForm.apelido) }}
                />

                <FormControl size="small" sx={{ flex: 2, minWidth: 220 }}>
                  <InputLabel sx={{ color: "var(--ig-t-ffffff)", fontWeight: "bold" }}>Empresa-Mãe / Grupo</InputLabel>
                  <Select
                    label="Empresa-Mãe / Grupo"
                    value={form.empresaMaeId}
                    onChange={(e) => atualizarCampo("empresaMaeId", e.target.value)}
                    inputProps={{ readOnly: somenteLeitura }}
                    sx={estiloSelect(somenteLeitura)}
                  >
                    <MenuItem value="">— Nenhuma —</MenuItem>
                    {opcoesEmpresaMae.map((e) => (
                      <MenuItem key={e.id} value={String(e.id)}>
                        {e.apelido}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                  <InputLabel sx={{ color: "var(--ig-t-ffffff)", fontWeight: "bold" }}>Situação*</InputLabel>
                  <Select
                    label="Situação*"
                    value={form.ativa ? "ATIVA" : "INATIVA"}
                    onChange={(e) => atualizarCampo("ativa", e.target.value === "ATIVA")}
                    inputProps={{ readOnly: somenteLeitura }}
                    sx={estiloSelect(somenteLeitura)}
                  >
                    <MenuItem value="ATIVA">Ativa</MenuItem>
                    <MenuItem value="INATIVA">Inativa</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Observações"
                size="small"
                multiline
                minRows={2}
                value={form.observacoes}
                onChange={(e) => atualizarCampo("observacoes", maiusculas(e.target.value))}
                slotProps={{ input: { readOnly: somenteLeitura } }}
                sx={estiloCampo(somenteLeitura, false)}
              />

              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                {/* Modo visualização */}
                {editandoId && somenteLeitura && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleCancelarForm}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Fechar
                    </Button>
                    {podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<FaEdit />}
                        onClick={handleHabilitarEdicao}
                        sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", py: 1, px: 3 }}
                      >
                        Editar
                      </Button>
                    )}
                    {podeExcluir && (
                      <Button
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleConfirmarDelete(editandoId)}
                        sx={{ width: "auto", backgroundColor: "#d32f2f", "&:hover": { backgroundColor: "#c62828" }, fontWeight: "bold", py: 1, px: 3 }}
                      >
                        Excluir
                      </Button>
                    )}
                  </>
                )}
                {/* Modo edição / criação */}
                {(!editandoId || !somenteLeitura) && (
                  <>
                    {editandoId && (
                      <Button
                        variant="outlined"
                        startIcon={<FaTimes />}
                        onClick={handleCancelarForm}
                        sx={{
                          width: "auto",
                          borderColor: "var(--ig-b-555555)",
                          color: "var(--ig-t-cccccc)",
                          "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                          fontWeight: "bold",
                          px: 3,
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                    {podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<FaSave />}
                        onClick={handleSalvar}
                        disabled={processando}
                        sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold", py: 1, px: 4 }}
                      >
                        {editandoId ? "Atualizar" : "Cadastrar"}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* ── Dialog: Confirmação de exclusão ──────────────────────────────── */}
      <Dialog open={confirmDelete.aberto} onClose={() => setConfirmDelete({ aberto: false, id: null })}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>Deseja excluir esta empresa? Esta ação não pode ser desfeita.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmDelete({ aberto: false, id: null })}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleExcluirConfirmado}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, aberto: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
